import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mail, Phone, MapPin, Linkedin, Facebook } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { getLocalizedRoute } from '@/lib/routes';

export const Footer = () => {
  const { t, language } = useLanguage();

  const footerContent = {
    en: {
      tagline: "Transform your pricing strategy with expert guidance",
      quickLinks: "Quick Links",
      services: "Our Services",
      contact: "Contact Info",
      followUs: "Follow Us",
      newsletter: "Stay Updated",
      newsletterDesc: "Get the latest pricing insights delivered to your inbox",
      emailPlaceholder: "Your email address",
      subscribe: "Subscribe",
      allRightsReserved: "All rights reserved.",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      offices: {
        title: "Our Offices",
        miami: "Miami, FL",
        mexico: "Mexico City, MX"
      }
    },
    es: {
      tagline: "Transforma tu estrategia de precios con orientación experta",
      quickLinks: "Enlaces Rápidos",
      services: "Nuestros Servicios",
      contact: "Información de Contacto",
      followUs: "Síguenos",
      newsletter: "Mantente Actualizado",
      newsletterDesc: "Recibe los últimos insights de precios en tu bandeja de entrada",
      emailPlaceholder: "Tu dirección de email",
      subscribe: "Suscribirse",
      allRightsReserved: "Todos los derechos reservados.",
      privacyPolicy: "Política de Privacidad",
      termsOfService: "Términos de Servicio",
      offices: {
        title: "Nuestras Oficinas",
        miami: "Miami, FL",
        mexico: "Ciudad de México, MX"
      }
    }
  };

  const current = footerContent[language];

  const navigationLinks = [
    { name: t.home, href: getLocalizedRoute('home', language) },
    { name: t.about, href: getLocalizedRoute('about', language) },
    { name: t.services, href: getLocalizedRoute('services', language) },
    { name: t.blog, href: getLocalizedRoute('blog', language) },
    { name: t.contact, href: getLocalizedRoute('contact', language) }
  ];

  const serviceLinks = [
    { name: t.smartPricing, href: getLocalizedRoute('smartPricing', language) },
    { name: t.consulting, href: getLocalizedRoute('consulting', language) },
    { name: t.research, href: getLocalizedRoute('research', language) }
  ];

  return (
    <footer className="bg-background border-t">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <img 
                  src="/lovable-uploads/0b1132d5-c978-428c-b4a4-cb6560dab03b.png" 
                  alt="PriceZing" 
                  className="h-10 w-auto mb-4"
                  width="176"
                  height="40"
                />
                <p className="text-muted-foreground max-w-md">
                  {current.tagline}
                </p>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-primary" />
                  <a href="mailto:info@pricezing.co" className="text-sm hover:text-primary transition-colors">
                    info@pricezing.co
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-primary" />
                  <a href="tel:+13467057320" className="text-sm hover:text-primary transition-colors">
                    +1 (346) 705-7320
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-primary mt-0.5" />
                  <div className="text-sm">
                    <div>16192 COASTAL HWY.</div>
                    <div>LEWES, DE 19958</div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-medium mb-3">{current.followUs}</h3>
                <div className="flex gap-3">
                  <a href="https://www.linkedin.com/company/pricezing/" target="_blank" rel="noopener noreferrer" aria-label="Visit PriceZing on LinkedIn">
                    <Button size="sm" variant="outline" className="w-9 h-9 p-0" aria-label="Visit PriceZing on LinkedIn">
                      <Linkedin className="w-4 h-4" />
                    </Button>
                  </a>
                  <a href="https://www.facebook.com/pricezing.co" target="_blank" rel="noopener noreferrer" aria-label="Visit PriceZing on Facebook">
                    <Button size="sm" variant="outline" className="w-9 h-9 p-0" aria-label="Visit PriceZing on Facebook">
                      <Facebook className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-medium mb-4">{current.quickLinks}</h3>
              <ul className="space-y-2">
                {navigationLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-medium mb-4">{current.services}</h3>
              <ul className="space-y-2">
                {serviceLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="font-medium mb-4">{current.newsletter}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {current.newsletterDesc}
              </p>
              <form className="space-y-2" onSubmit={(e) => {
                e.preventDefault();
                const email = (e.target as HTMLFormElement).email.value;
                window.open(`mailto:info@pricezing.co?subject=Newsletter Subscription&body=Please add ${email} to the newsletter.`, '_blank');
              }}>
                <input
                  name="email"
                  type="email"
                  placeholder={current.emailPlaceholder}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  required
                />
                <Button type="submit" size="sm" className="w-full">
                  {current.subscribe}
                </Button>
              </form>
            </div>
          </div>
        </div>

        <Separator />

        {/* Bottom Footer */}
        <div className="py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              © 2024 PriceZing Solutions. {current.allRightsReserved}
            </div>
            <div className="flex gap-6">
              <Link to={getLocalizedRoute('privacyPolicy', language)} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                {current.privacyPolicy}
              </Link>
              <Link to={getLocalizedRoute('termsOfService', language)} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                {current.termsOfService}
              </Link>
              <Link to={getLocalizedRoute('cookiePolicy', language)} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Cookies
              </Link>
              <Link to={getLocalizedRoute('disclaimer', language)} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                {language === 'en' ? 'Disclaimer' : 'Descargo'}
              </Link>
              <Link to={getLocalizedRoute('dataProcessing', language)} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                {language === 'en' ? 'GDPR' : 'RGPD'}
              </Link>
            </div>
          </div>
        </div>

        <Separator />

        {/* Development Credit */}
        <div className="py-3">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Developed by{' '}
              <a 
                href="https://rocwwa.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                ROC Worldwide Agency LLC
              </a>
              {' '} / SEO Optimized by{' '}
              <a 
                href="https://luisroc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Luis ROC
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};